// =============================================================================
// legal-admin-client.http.ts — Adaptateur HTTP RÉEL du LegalAdminClient (S5bis + A-CGU ÉTAPE 2).
// =============================================================================
// Remplace le stub-first derrière le MÊME DI seam (LegalAdminClientProvider) : les hooks/
// écrans consomment l'interface LegalAdminClient, l'impl est substituée sans toucher l'UI.
//
// AUTORITÉ = API (NestJS, AdminSessionGuard, lecture cross-tenant sous withAdmin, RLS).
// Cet adaptateur ne porte AUCUNE règle métier : il parse la réponse et mappe les erreurs.
// L'anti-join non-signataires reste composé côté FRONT (avec le SupplierClient), jamais ici.
//
// SÉCURITÉ (RULE_REF_006) : la session admin est portée par le COOKIE httpOnly posé par
// l'API (`credentials: 'include'`) — l'UI ne lit ni n'écrit le secret de session.
// LECTURES (GET) : aucun en-tête CSRF. MUTATIONS (POST/DELETE) : en-tête double-submit
// `x-csrf-token` = cookie NON-httpOnly `admin_csrf` (parité HttpCampaignClient / catalog-edition).
// Sans l'en-tête, toute écriture est 403 (fail-closed serveur).
//
// ROUTES : les LECTURES registre + DELETE brouillon sont sous `/admin/legal/cgu/*`
// (LegalAdminController) ; la CRÉATION de brouillon et la PUBLICATION sont sous `/legal/cgu/*`
// (LegalController, admin-guarded) — deux préfixes distincts, MÊME session admin + `admin_csrf`.

import {
  LegalAdminApiError,
  type AdminSignaturesJournalDto,
  type AdminSignedContactsDto,
  type AdminSignedSiretsDto,
  type AdminSignedStatusBySiretDto,
  type CguRegistreVersionDetailDto,
  type CguRegistreVersionRowDto,
  type CreateCguDraftInput,
  type EditCguDraftInput,
  type ForcedResignatureDto,
  type LegalAdminClient,
} from '@/api/legal-admin-client';

/** Forme du corps d'erreur structuré NestJS (`{ statusCode, code?, message }`). */
interface StructuredErrorBody {
  readonly statusCode?: number;
  readonly code?: string;
  readonly message?: string;
}

/** Nom du cookie CSRF NON-httpOnly posé au login admin (double-submit, cf. API CSRF_COOKIE). */
const CSRF_COOKIE_NAME = 'admin_csrf';

/**
 * Lit la valeur du cookie CSRF `admin_csrf` (NON-httpOnly, lisible par le SPA même origine).
 * `undefined` si absent (pas de session, ou contexte sans document) — l'appel mutant partira
 * alors sans en-tête et l'API répondra 403 (fail-closed serveur). Parité stricte catalog-edition.
 */
function readCsrfToken(): string | undefined {
  if (typeof document === 'undefined') {
    return undefined;
  }
  for (const part of document.cookie.split(';')) {
    const [rawName, ...rest] = part.trim().split('=');
    if (rawName === CSRF_COOKIE_NAME) {
      return decodeURIComponent(rest.join('='));
    }
  }
  return undefined;
}

/**
 * Adaptateur HTTP réel du client CGU ADMIN. `baseUrl` par défaut `/api` (SPA et API partagent
 * l'origine, ou proxy). En test, fetch est mocké.
 */
export class HttpLegalAdminClient implements LegalAdminClient {
  public constructor(private readonly baseUrl: string = '/api') {}

  public async signedStatusBySiret(): Promise<AdminSignedStatusBySiretDto> {
    return this.get<AdminSignedStatusBySiretDto>('/admin/legal/cgu/signed-status');
  }

  public async signaturesJournal(): Promise<AdminSignaturesJournalDto> {
    return this.get<AdminSignaturesJournalDto>('/admin/legal/cgu/signatures');
  }

  public async signedSiretsForCurrentVersion(): Promise<AdminSignedSiretsDto> {
    return this.get<AdminSignedSiretsDto>('/admin/legal/cgu/signed-sirets');
  }

  public async signedContactsForCurrentVersion(): Promise<AdminSignedContactsDto> {
    return this.get<AdminSignedContactsDto>('/admin/legal/cgu/signed-contacts');
  }

  public async listVersions(): Promise<readonly CguRegistreVersionRowDto[]> {
    return this.get<readonly CguRegistreVersionRowDto[]>('/admin/legal/cgu/versions');
  }

  public async countVersions(): Promise<{ readonly count: number }> {
    return this.get<{ readonly count: number }>('/admin/legal/cgu/versions/count');
  }

  public async getVersion(cguVersionId: string): Promise<CguRegistreVersionDetailDto> {
    return this.get<CguRegistreVersionDetailDto>(
      `/admin/legal/cgu/versions/${encodeURIComponent(cguVersionId)}`,
    );
  }

  public async getVersionPdf(cguVersionId: string): Promise<ArrayBuffer> {
    // OCTETS (application/pdf) : on lit arrayBuffer(), PAS json(). 404 (Brouillon / sans PDF).
    const response = await fetch(
      `${this.baseUrl}/admin/legal/cgu/versions/${encodeURIComponent(cguVersionId)}/pdf`,
      { method: 'GET', credentials: 'include' },
    );
    if (!response.ok) {
      throw await this.toApiError(response);
    }
    return response.arrayBuffer();
  }

  public async saveDraft(
    input: CreateCguDraftInput,
  ): Promise<{ readonly cguVersionId: string }> {
    // POST /legal/cgu/versions — le back (ca0aa8b) persiste versionNumber/changelog/effectiveDate
    // ET `contentHtml` (nettoyé serveur). 400 si le HTML est aberrant (CguContentHtmlInvalidError).
    return this.mutate<{ readonly cguVersionId: string }>('POST', '/legal/cgu/versions', {
      versionNumber: input.versionNumber,
      changelog: input.changelog,
      ...(input.effectiveDate !== undefined ? { effectiveDate: input.effectiveDate } : {}),
      ...(input.contentHtml !== undefined ? { contentHtml: input.contentHtml } : {}),
    });
  }

  public async editDraft(
    cguVersionId: string,
    input: EditCguDraftInput,
  ): Promise<{ readonly cguVersionId: string; readonly state: string }> {
    // PATCH /admin/legal/cgu/versions/:id — patch PARTIEL du corps d'un Brouillon (ca0aa8b).
    // Le back nettoie le HTML. 404 (absent) / 409 (non-Brouillon) / 400 (HTML aberrant).
    return this.mutate<{ readonly cguVersionId: string; readonly state: string }>(
      'PATCH',
      `/admin/legal/cgu/versions/${encodeURIComponent(cguVersionId)}`,
      {
        ...(input.contentHtml !== undefined ? { contentHtml: input.contentHtml } : {}),
        ...(input.effectiveDate !== undefined ? { effectiveDate: input.effectiveDate } : {}),
      },
    );
  }

  public async publishVersion(
    cguVersionId: string,
  ): Promise<{ readonly state: string }> {
    return this.mutate<{ readonly state: string }>(
      'POST',
      `/legal/cgu/versions/${encodeURIComponent(cguVersionId)}/publish`,
    );
  }

  public async deleteDraft(cguVersionId: string): Promise<void> {
    // 204 No Content en succès (aucun corps à parser).
    await this.mutate<void>(
      'DELETE',
      `/admin/legal/cgu/versions/${encodeURIComponent(cguVersionId)}`,
      undefined,
      { expectNoContent: true },
    );
  }

  public async relanceCgu(siret: string): Promise<void> {
    // POST /admin/legal/cgu/relance/:siret — 202 Accepted, corps vide (best-effort, async via event).
    // MUTATION → en-tête double-submit `x-csrf-token` posé par `mutate`. 409 si déjà signataire.
    await this.mutate<void>(
      'POST',
      `/admin/legal/cgu/relance/${encodeURIComponent(siret)}`,
      undefined,
      { expectNoContent: true },
    );
  }

  public async forceResignature(siret: string, motif: string): Promise<void> {
    // POST /admin/legal/cgu/resignature/:siret — 202 Accepted, corps vide (R15-GOV-04). Le MOTIF (PII)
    // transite dans le CORPS POST `{ motif }`, jamais dans l'URL. MUTATION → en-tête double-submit
    // `x-csrf-token` posé par `mutate`. 400 si SIRET malformé ou motif absent/vide (garde serveur).
    await this.mutate<void>(
      'POST',
      `/admin/legal/cgu/resignature/${encodeURIComponent(siret)}`,
      { motif },
      { expectNoContent: true },
    );
  }

  public async listForcedResignatures(
    siret: string,
  ): Promise<readonly ForcedResignatureDto[]> {
    // GET /admin/legal/cgu/resignature/:siret — journal des re-signatures forcées (motif EN CLAIR,
    // admin-relisible). LECTURE (calque `listVersions`) : aucun en-tête CSRF. 400 si SIRET malformé.
    return this.get<readonly ForcedResignatureDto[]>(
      `/admin/legal/cgu/resignature/${encodeURIComponent(siret)}`,
    );
  }

  /** GET same-origin (cookie httpOnly), parse le JSON typé, ou lève une LegalAdminApiError. */
  private async get<T>(path: string): Promise<T> {
    const response = await fetch(`${this.baseUrl}${path}`, {
      method: 'GET',
      credentials: 'include',
      headers: { 'content-type': 'application/json' },
    });
    if (!response.ok) {
      throw await this.toApiError(response);
    }
    return (await response.json()) as T;
  }

  /**
   * Mutation same-origin : pose l'en-tête double-submit `x-csrf-token` = cookie `admin_csrf`.
   * Parse le JSON typé (sauf 204 : `expectNoContent`). Erreur HTTP → LegalAdminApiError.
   */
  private async mutate<T>(
    method: 'POST' | 'PATCH' | 'DELETE',
    path: string,
    body?: Record<string, unknown>,
    options: { readonly expectNoContent?: boolean } = {},
  ): Promise<T> {
    const headers: Record<string, string> = { 'content-type': 'application/json' };
    const csrf = readCsrfToken();
    if (csrf !== undefined) {
      headers['x-csrf-token'] = csrf;
    }
    const response = await fetch(`${this.baseUrl}${path}`, {
      method,
      credentials: 'include',
      headers,
      ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
    });
    if (!response.ok) {
      throw await this.toApiError(response);
    }
    if (options.expectNoContent === true) {
      return undefined as T;
    }
    return (await response.json()) as T;
  }

  /** Convertit une réponse d'erreur en LegalAdminApiError (message serveur, sinon repli). */
  private async toApiError(response: Response): Promise<LegalAdminApiError> {
    let body: StructuredErrorBody | undefined;
    try {
      body = (await response.json()) as StructuredErrorBody;
    } catch {
      body = undefined;
    }
    const message =
      body?.message ??
      `Erreur API (${String(response.status)}) sur le registre CGU admin.`;
    // Le `status` HTTP permet à l'UI de mapper par code (400 masqué, 404/409 relayés).
    return new LegalAdminApiError(message, response.status);
  }
}
